/**
 * Jotai Actions - Async state updates
 * 
 * Functions that perform side effects and update atoms
 */

import axios from 'axios';
import { SetStateAction, WritableAtom } from 'jotai';
import { AdminApiTypes, IAdmin, IUser } from 'common';
import {
  adminAtom,
  isAuthenticatedAtom,
  authLoadingAtom,
  appLoadedAtom,
  usersAtom,
  usersLoadingAtom,
  usersTotalAtom,
  administratorsAtom,
  administratorsLoadingAtom,
  administratorsTotalAtom,
  groupsAtom,
  groupsLoadingAtom,
  groupsTotalAtom,
  crawlingsAtom,
  crawlingsLoadingAtom,
  alertAtom,
} from './atoms';
import api from '../services/api';
import { csrfManager } from '../services/csrf';

type WritableSetAtom<Value> = WritableAtom<Value, [SetStateAction<Value>], unknown>;
export type Setter = <Value>(atom: WritableSetAtom<Value>, value: SetStateAction<Value>) => unknown;

// ============================================================================
// Admin/Auth Actions
// ============================================================================

export const loadAdminInfo = async (set: Setter): Promise<boolean> => {
  set(authLoadingAtom, true);
  try {
    // Initialize CSRF token
    await csrfManager.initialize();
    
    // Fetch admin info
    const adminData = await api.getAdmin();
    set(adminAtom, adminData.admin as IAdmin);
    set(isAuthenticatedAtom, true);
    return true;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      set(adminAtom, null);
      set(isAuthenticatedAtom, false);
      return false;
    }
    console.error('Failed to load admin info:', error);
    set(isAuthenticatedAtom, false);
    throw error;
  } finally {
    set(authLoadingAtom, false);
  }
};

export const logout = async (set: Setter) => {
  try {
    await api.logout();
    set(adminAtom, null);
    set(isAuthenticatedAtom, false);
    csrfManager.clearToken();
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

export const setAppLoaded = (set: Setter) => {
  set(appLoadedAtom, true);
};

// ============================================================================
// Users Actions
// ============================================================================

export const fetchUsers = async (set: Setter, filters: { name: string }, options: { skip?: number; limit?: number }) => {
  set(usersLoadingAtom, true);
  try {
    const response = await api.getUsers({ filters, options });
    set(usersAtom, response.users);
    set(usersTotalAtom, response.total);
  } catch (error) {
    console.error('Failed to fetch users:', error);
    set(alertAtom, { message: 'Failed to load users', severity: 'error' });
    throw error;
  } finally {
    set(usersLoadingAtom, false);
  }
};

export const createUser = async (set: Setter, doc: Partial<IUser>) => {
  try {
    const result = await api.createUser(doc);
    set(alertAtom, { message: 'User created successfully', severity: 'success' });
    return result;
  } catch (error) {
    console.error('Failed to create user:', error);
    set(alertAtom, { message: 'Failed to create user', severity: 'error' });
    throw error;
  }
};

export const updateUser = async (set: Setter, userId: string, doc: Partial<IUser>) => {
  try {
    const result = await api.updateUser(userId, doc);
    set(alertAtom, { message: 'User updated successfully', severity: 'success' });
    return result;
  } catch (error) {
    console.error('Failed to update user:', error);
    set(alertAtom, { message: 'Failed to update user', severity: 'error' });
    throw error;
  }
};

export const removeUser = async (set: Setter, userId: string) => {
  try {
    await api.removeUser(userId);
    set(alertAtom, { message: 'User removed successfully', severity: 'success' });
  } catch (error) {
    console.error('Failed to remove user:', error);
    set(alertAtom, { message: 'Failed to remove user', severity: 'error' });
    throw error;
  }
};

// ============================================================================
// Administrators Actions
// ============================================================================

export const fetchAdministrators = async (set: Setter, filters: { name: string }, options: { skip?: number; limit?: number }) => {
  set(administratorsLoadingAtom, true);
  try {
    const response = await api.getAdmins({ filters, options });
    set(administratorsAtom, response.admins);
    set(administratorsTotalAtom, response.total);
  } catch (error) {
    console.error('Failed to fetch administrators:', error);
    set(alertAtom, { message: 'Failed to load administrators', severity: 'error' });
    throw error;
  } finally {
    set(administratorsLoadingAtom, false);
  }
};

export const createAdministrator = async (set: Setter, doc: Partial<IAdmin>) => {
  try {
    const result = await api.createAdmin(doc);
    set(alertAtom, { message: 'Administrator created successfully', severity: 'success' });
    return result;
  } catch (error) {
    console.error('Failed to create administrator:', error);
    set(alertAtom, { message: 'Failed to create administrator', severity: 'error' });
    throw error;
  }
};

export const removeAdministrator = async (set: Setter, adminID: string) => {
  try {
    await api.removeAdmin(adminID);
    set(alertAtom, { message: 'Administrator removed successfully', severity: 'success' });
  } catch (error) {
    console.error('Failed to remove administrator:', error);
    set(alertAtom, { message: 'Failed to remove administrator', severity: 'error' });
    throw error;
  }
};

// ============================================================================
// Groups Actions
// ============================================================================

export const fetchGroups = async (set: Setter, filters: { name: string }, options: { skip?: number; limit?: number }) => {
  set(groupsLoadingAtom, true);
  try {
    const response = await api.getGroups({ filters, options });
    set(groupsAtom, response.groups);
    set(groupsTotalAtom, response.total);
  } catch (error) {
    console.error('Failed to fetch groups:', error);
    set(alertAtom, { message: 'Failed to load groups', severity: 'error' });
    throw error;
  } finally {
    set(groupsLoadingAtom, false);
  }
};

export const createGroup = async (set: Setter, body: AdminApiTypes.BodyCreateGroup) => {
  try {
    const result = await api.createGroup(body);
    set(alertAtom, { message: 'Group created successfully', severity: 'success' });
    return result;
  } catch (error) {
    console.error('Failed to create group:', error);
    set(alertAtom, { message: 'Failed to create group', severity: 'error' });
    throw error;
  }
};

export const updateGroup = async (set: Setter, id: string, body: AdminApiTypes.BodyUpdateGroup) => {
  try {
    const result = await api.updateGroup(id, body);
    set(alertAtom, { message: 'Group updated successfully', severity: 'success' });
    return result;
  } catch (error) {
    console.error('Failed to update group:', error);
    set(alertAtom, { message: 'Failed to update group', severity: 'error' });
    throw error;
  }
};

export const deleteGroup = async (set: Setter, id: string) => {
  try {
    await api.deleteGroup(id);
    set(alertAtom, { message: 'Group deleted successfully', severity: 'success' });
  } catch (error) {
    console.error('Failed to delete group:', error);
    set(alertAtom, { message: 'Failed to delete group', severity: 'error' });
    throw error;
  }
};

// ============================================================================
// Crawlings Actions
// ============================================================================

export const fetchCrawlings = async (set: Setter, query: string) => {
  set(crawlingsLoadingAtom, true);
  try {
    const crawlings = await api.getCrawlings(query);
    set(crawlingsAtom, crawlings);
  } catch (error) {
    console.error('Failed to fetch crawlings:', error);
    set(alertAtom, { message: 'Failed to load crawlings', severity: 'error' });
    throw error;
  } finally {
    set(crawlingsLoadingAtom, false);
  }
};

// ============================================================================
// Alert Actions
// ============================================================================

export const showAlert = (set: Setter, message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
  set(alertAtom, { message, severity });
};

export const clearAlert = (set: Setter) => {
  set(alertAtom, null);
};
